/* Native phone gallery for the product page (2026-08-19).
   ------------------------------------------------------------------
   WHAT THIS IS
   The product gallery normally runs on tiny-slider, which animates
   with a fixed-duration ease: a hard flick and a lazy push settle in
   exactly the same 450ms, so the carousel never feels like it has
   weight. mykitsch.com (theme "Unick": Tailwind + Alpine + keen-slider)
   reads better because its slider is velocity-aware.

   Rather than hand-roll spring physics, this hands the job to the one
   engine that already has them and runs off the main thread: the
   browser's own scroll, with CSS scroll-snap deciding where to land.
   That IS the momentum + spring settle, for free, at 60fps.

   WHY IT LOOKS LIKE A TINY-SLIDER
   Eight things in this theme are wired to the gallery - the dots and
   counter, variant image jumps (19 call sites), the thumbnail strip,
   video pausing, pinch zoom, the sticky gallery, and BOTH personalizer
   galleries. Rewriting each was the risky path. So this module
   impersonates tiny-slider instead:

     - the DOM keeps the classes those consumers look for
       (.tns-item on every slide, .tns-slide-active on the current one)
     - the returned object answers the same calls
       (getInfo / goTo / destroy / rebuild / events.on)

   With those two contracts held, every consumer keeps working with no
   edits at all. See scratchpad/native-gallery-contract.md for the full
   consumer map behind this decision.

   SCOPE
   Phones only, main product gallery only, and only when the section
   setting asks for it - mount() returns null for anything else and the
   caller falls straight through to tns(). That null is the rollback
   path: flip the setting and this file stops participating. */
(function () {
  'use strict';

  if (window.LHNativeGallery) return;

  var ACTIVE = 'tns-slide-active';
  var ITEM = 'tns-item';
  var TRACK_FLAG = 'lh-ngal';

  function clamp(n, lo, hi) {
    return n < lo ? lo : (n > hi ? hi : n);
  }

  function Emitter() {
    this._h = {};
  }
  Emitter.prototype.on = function (name, fn) {
    (this._h[name] || (this._h[name] = [])).push(fn);
  };
  Emitter.prototype.off = function (name, fn) {
    var a = this._h[name];
    if (!a) return;
    var i = a.indexOf(fn);
    if (i > -1) a.splice(i, 1);
  };
  Emitter.prototype.emit = function (name, payload) {
    var a = this._h[name];
    if (!a) return;
    for (var i = 0; i < a.length; i++) {
      /* One bad listener must not stop the others, or a single throw
         from an app script would freeze the dots for the session. */
      try { a[i](payload); } catch (e) {}
    }
  };

  function mount(section, opts) {
    opts = opts || {};

    /* --- eligibility ------------------------------------------------
       Anything unexpected returns null so the caller runs tiny-slider,
       which is the behaviour this store shipped with. */
    if (!section || section.dataset.lhNative !== 'true') return null;
    /* Same breakpoint as the CSS, expressed the same way, so the layout
       rules and the engine can never disagree about what a phone is. */
    if (!window.matchMedia('(max-width: 749.98px)').matches) return null;
    if (!('scrollBehavior' in document.documentElement.style)) return null;

    var track = section.querySelector('.slide-container');
    if (!track) return null;

    var slides = Array.prototype.slice.call(track.children);
    if (!slides.length) return null;

    /* --- prepare the DOM --------------------------------------------
       tns-initialized is not decoration: variable.css carries a block of
       "Critical load" rules keyed on .slide-container:not(.tns-initialized)
       which hide every slide but the first, stack the row in a column and
       set overflow:hidden. That is the theme's anti-flicker state, meant to
       show one still image until a slider takes over, and tiny-slider
       leaves it behind by adding this class (theme.js tnsInitialized).
       Without it the row stays frozen: one image, nothing to scroll
       sideways - which is exactly how this shipped the first time. */
    track.classList.add(TRACK_FLAG, 'tns-initialized');
    /* The section becomes the clipping window for the transformed row. */
    section.classList.add('lh-ngal-host');
    /* tiny-slider normally adds this; the CSS that lays the row out
       horizontally keys off it, and consumers use it to find a slide
       (lh-tee-pinned-gallery reads .closest('.tns-item')). */
    for (var i = 0; i < slides.length; i++) slides[i].classList.add(ITEM);

    /* The wishlist heart is deliberately left alone: it stays the theme's
       single button pinned to the gallery frame (.wishlist-btn--gallery),
       floating over the row while the images slide beneath it. A version
       that cloned a heart into every slide was tried and reverted on
       2026-08-19 - the merchant prefers the floating one. Its mobile
       offset lives in lh-custom.css. */


    var events = new Emitter();
    var index = 0;
    var destroyed = false;
    var offset = 0;          /* px the row is currently shifted left */
    var animating = false;

    /* --- geometry ---------------------------------------------------- */
    function step() {
      if (slides.length > 1) {
        var d = slides[1].offsetLeft - slides[0].offsetLeft;
        if (d > 0) return d;
      }
      return slides[0] ? slides[0].offsetWidth : 0;
    }

    function maxOffset() {
      return Math.max(0, (slides.length - 1) * step());
    }

    function draw(px, ms) {
      offset = px;
      track.style.transition = ms
        ? 'transform ' + ms + 'ms cubic-bezier(.23,1,.32,1)'
        : 'none';
      track.style.transform = 'translate3d(' + (-px) + 'px,0,0)';
    }

    function paint(i) {
      for (var n = 0; n < slides.length; n++) {
        slides[n].classList.toggle(ACTIVE, n === i);
      }
    }

    function info() {
      return {
        container: track,
        slideCount: slides.length,
        index: index,
        /* 1-based on purpose: the dots and the "3 / 28" counter both do
           displayIndex - 1, matching tiny-slider. */
        displayIndex: index + 1,
        slideItems: slides,
        isNative: true
      };
    }

    function settleTo(i, ms) {
      i = clamp(i, 0, slides.length - 1);
      var changed = i !== index;
      index = i;
      animating = true;
      draw(i * step(), ms);
      paint(index);
      if (changed) {
        /* indexChanged pauses a video that has scrolled away and refreshes
           the counter; touchEnd is what the theme binds to keep the
           thumbnail strip in step (SlideSection.onSlideChange). */
        events.emit('indexChanged', info());
        events.emit('touchEnd', info());
      }
      window.setTimeout(function () { animating = false; }, ms || 0);
    }

    /* --- drag --------------------------------------------------------
       The row is moved by this script, not by the browser's scrolling.
       That is the whole point: with touch-action pan-y the browser owns
       vertical absolutely and hands it to the page the instant a finger
       moves down, while horizontal never belongs to a scroll container at
       all. A native scroller could not do that - once iOS had scrolled it
       sideways it kept ownership of the gesture, so the next touch on the
       image was swallowed instead of scrolling the page, and only the one
       after it worked (reported 2026-08-19). mykitsch.com avoids the same
       trap the same way: its slider is transform-driven with
       touch-action: pan-y. */
    var drag = null;

    function onStart(e) {
      if (destroyed || !e.touches || e.touches.length !== 1) return;
      var t = e.touches[0];
      drag = {
        x0: t.clientX, y0: t.clientY,
        base: offset,
        axis: '',            /* decided on the first real movement */
        last: t.clientX, lastT: e.timeStamp || Date.now(), v: 0
      };
      /* A touch during the settle stops it where it is, so the row feels
         catchable rather than committed - and, unlike a native scroller,
         that touch is still free to become a page scroll. */
      if (animating) {
        var m = /translate3d\(\s*(-?[\d.]+)px/.exec(track.style.transform || '');
        if (m) { offset = -parseFloat(m[1]); drag.base = offset; }
        draw(offset, 0);
        animating = false;
      }
    }

    function onMove(e) {
      if (!drag || !e.touches || e.touches.length !== 1) return;
      var t = e.touches[0];
      var dx = t.clientX - drag.x0;
      var dy = t.clientY - drag.y0;

      if (!drag.axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        /* Whichever way the finger actually went. Vertical is handed
           straight back to the browser and never looked at again for this
           gesture, so a downward drag scrolls the page immediately. */
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (drag.axis === 'y') { drag = null; return; }
      }

      var next = drag.base - dx;
      var max = maxOffset();
      /* Rubber band past either end rather than a dead stop. */
      if (next < 0) next = next * 0.35;
      else if (next > max) next = max + (next - max) * 0.35;

      var now = e.timeStamp || Date.now();
      var dt = now - drag.lastT;
      if (dt > 0) drag.v = (t.clientX - drag.last) / dt;
      drag.last = t.clientX;
      drag.lastT = now;

      draw(next, 0);
      if (e.cancelable) e.preventDefault();
    }

    function onEnd(e) {
      if (!drag) return;
      var s = step();
      var moved = drag.base - offset;
      var target = index;
      /* A flick counts on its own, however short; otherwise it takes a
         third of an image to commit. Velocity is px/ms. */
      if (drag.v < -0.35 || moved > s * 0.33) target = index + 1;
      else if (drag.v > 0.35 || moved < -s * 0.33) target = index - 1;

      /* Faster flick, shorter settle - this is what reads as weight. */
      var speed = Math.min(Math.abs(drag.v), 2);
      var ms = Math.round(520 - speed * 150);
      drag = null;
      settleTo(target, clamp(ms, 260, 560));
    }

    track.addEventListener('touchstart', onStart, { passive: true });
    track.addEventListener('touchmove', onMove, { passive: false });
    track.addEventListener('touchend', onEnd, { passive: true });
    track.addEventListener('touchcancel', onEnd, { passive: true });

    function onResize() {
      if (destroyed) return;
      draw(index * step(), 0);
    }
    window.addEventListener('resize', onResize);

    /* Opening slide. A variant with its own featured image asks for a
       specific one, and that jump must not be animated. */
    var start = clamp(parseInt(opts.startIndex, 10) || 0, 0, slides.length - 1);
    index = start;
    paint(index);
    /* Waits a frame: at mount the images may still be sizing, and a
       transform written against a half-built row lands nowhere. */
    window.requestAnimationFrame(function () {
      if (!destroyed) draw(index * step(), 0);
    });

    return {
      isNative: true,
      events: events,
      getInfo: info,

      goTo: function (target) {
        if (destroyed) return;
        var i;
        if (target === 'next') i = index + 1;
        else if (target === 'prev') i = index - 1;
        else if (target === 'first') i = 0;
        else if (target === 'last') i = slides.length - 1;
        else i = parseInt(target, 10);
        if (isNaN(i)) return;
        settleTo(i, 420);
      },

      /* The theme rebuilds sliders on breakpoint changes; both of these
         exist so SlideSection.refresh() and .destroy() work unchanged. */
      rebuild: function () {
        slides = Array.prototype.slice.call(track.children);
        for (var n = 0; n < slides.length; n++) slides[n].classList.add(ITEM);
        index = clamp(index, 0, slides.length - 1);
        paint(index);
        draw(index * step(), 0);
      },

      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        track.removeEventListener('touchstart', onStart);
        track.removeEventListener('touchmove', onMove);
        track.removeEventListener('touchend', onEnd);
        track.removeEventListener('touchcancel', onEnd);
        window.removeEventListener('resize', onResize);
        track.style.transition = '';
        track.style.transform = '';
        /* tns-initialized goes back too, so a rebuild that hands the row to
           tiny-slider starts from the same pre-init state the page loaded
           with rather than a half-dismantled one. */
        track.classList.remove(TRACK_FLAG, 'tns-initialized');
        section.classList.remove('lh-ngal-host');
        for (var n = 0; n < slides.length; n++) {
          slides[n].classList.remove(ITEM, ACTIVE);
        }
      }
    };
  }

  window.LHNativeGallery = { mount: mount };
})();
